/**
 * ShareButton — small "Share" affordance in the CardShell footer.
 *
 * Two distinct share modes, picked per-card by what the backend set:
 *
 *   1. LIVE URL mode (path is set)
 *      Card content is evergreen / stable enough that a memorable
 *      URL like /p/swampert/great works.  Click → copy the absolute
 *      URL to clipboard (or open the Web Share API on mobile).
 *
 *   2. SNAPSHOT mode (path is empty + snapshotPayload provided)
 *      Card content is time-sensitive (raids today, cups today, etc.)
 *      so the URL has to capture "what I saw" instead of "this query."
 *      Click → POST the full VisualPayload to /share, get back a
 *      /s/<token> URL, copy that.  Recipient sees the exact card with
 *      a "Captured X ago" banner.
 *
 * Renders nothing in either mode when its inputs aren't ready.
 *
 * Phase A landed live-URL mode for evergreen cards.  Phase C adds
 * snapshot mode for the 5 state-of-today card types.
 */
import { useState, useCallback } from "react";
import type { VisualPayload } from "../../types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

interface ShareButtonProps {
  /** Path-only live URL from VisualPayload.share_url (e.g. "/p/swampert").
   * Take precedence over snapshot mode when set. */
  path: string;
  /** Used as the share-sheet title — typically the card's title. */
  title?: string;
  /** When path is empty AND this is provided, the button switches to
   * snapshot mode.  We POST this payload to /share, capture the
   * resulting token, and copy /s/<token>. */
  snapshotPayload?: VisualPayload;
  /** Forwarded to /share so the snapshot page can offer a "Ask this
   * same question now" CTA.  Optional. */
  snapshotQuestion?: string;
  /** Forwarded to /share so the snapshot page can render the spoken
   * answer below the card.  Optional. */
  snapshotVoiceText?: string;
}

export default function ShareButton({
  path,
  title,
  snapshotPayload,
  snapshotQuestion,
  snapshotVoiceText,
}: ShareButtonProps) {
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Either we have a live URL OR a snapshot-eligible payload.  Nothing
  // to render otherwise — keeps cards quiet that don't yet have a
  // matching live route AND aren't time-sensitive.
  const canShare = !!path || !!snapshotPayload;

  const handleShare = useCallback(async () => {
    if (!canShare || busy) return;
    setBusy(true);
    try {
      let url: string | null = null;

      if (path) {
        // LIVE URL mode — straightforward path concatenation.
        url = `${window.location.origin}${path}`;
      } else if (snapshotPayload) {
        // SNAPSHOT mode — POST to /share, get the token URL back.
        // We use the backend's PUBLIC_BASE_URL-derived absolute URL
        // (returned in `url` of ShareCreateResponse) when available;
        // otherwise fall back to window.location.origin + /s/<token>.
        try {
          const resp = await fetch(`${BASE_URL}/share`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              visual: snapshotPayload,
              voice_text: snapshotVoiceText,
              question: snapshotQuestion,
            }),
          });
          if (!resp.ok) {
            throw new Error(`share endpoint returned ${resp.status}`);
          }
          const data = (await resp.json()) as { token: string; url: string };
          url = data.url || `${window.location.origin}/s/${data.token}`;
        } catch (err) {
          console.warn("snapshot create failed:", err);
          setToast("Couldn't create snapshot — try again");
          setTimeout(() => setToast(null), 2400);
          return;
        }
      }

      if (!url) return;

      const shareTitle = title || "ScoutDex card";

      // Web Share API first — opens the OS share sheet on mobile.
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        try {
          await navigator.share({ title: shareTitle, url });
          return;
        } catch (err) {
          const name = (err as { name?: string })?.name;
          if (name === "AbortError") return; // user explicitly canceled — don't auto-copy
          // Other share errors: fall through to clipboard copy.
        }
      }

      // Clipboard fallback.
      try {
        await navigator.clipboard.writeText(url);
        setToast(snapshotPayload && !path ? "Snapshot link copied — expires in 30 days" : "Link copied");
        setTimeout(() => setToast(null), 2400);
      } catch {
        // Last-resort: do nothing.  Modern browsers all support clipboard API.
      }
    } finally {
      setBusy(false);
    }
  }, [canShare, busy, path, snapshotPayload, snapshotVoiceText, snapshotQuestion, title]);

  if (!canShare) return null;

  return (
    <span className="inline-flex items-center gap-1.5 relative">
      <button
        type="button"
        onClick={handleShare}
        disabled={busy}
        aria-label="Share this card"
        title={path ? "Share this card" : "Snapshot this card (30-day link)"}
        className="text-white/30 hover:text-white/70 transition-colors underline-offset-2 hover:underline disabled:opacity-40 disabled:cursor-progress"
      >
        Share
      </button>
      {toast && (
        <span
          role="status"
          aria-live="polite"
          className="absolute -top-7 right-0 whitespace-nowrap text-[10px] bg-scout-elevated border border-white/[0.12] text-white/85 px-2 py-1 rounded-md shadow-lg animate-fade-in"
        >
          {toast}
        </span>
      )}
    </span>
  );
}
